import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsightsIcon from "@mui/icons-material/Insights";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { ErrorBoundary } from "@sentry/react";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { fetchCurrentUserDetails } from "../../actions/userActions";
import { updateUserDetails } from "../../api";
import { ErrorMsg } from "../../components/AppMessages";
import { BusinessFunctionInput, DarkLightToggle, TextInput } from "../../components/Forms/common";
import { CedarShellLayout } from "../../components/Layouts";
import { CATEGORY_COLORS, DEFAULT_CATEGORY_COLOR, getPrimaryCategory, isArchivedDashboard } from "../../constants";
import { useHasView } from "../../hooks";
import { useDashboardRegistry } from "../../hooks/react-query/dashboards/useDashboardRegistry";
import { ProfilePicture } from "../UserProfile/common/ProfilePicture";
import { getChangedFields } from "../../utils/getChangedFields";
import { CollapsibleCategorySection } from "./components/CollapsibleCategorySection";
import { DashboardAccessSection } from "./components/DashboardAccessSection";
import { EngagementCard } from "./components/EngagementCard";
import "../../assets/styles/pages/settingsPage.scss";

// Every field this page's own form controls can actually change - email/role are shown but
// readonly, and everything else on IUserDetails (id, groups, views, account_status, ...) isn't
// exposed to any control here at all, so none of those belong in a diff against what this page
// itself could have changed.
const EDITABLE_FIELDS = ["first_name", "last_name", "business_function", "department"] as const;

export const SettingsPage = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootStateOrAny) => state.user.details);
  const { dashboards } = useDashboardRegistry();
  // A capability view, not tied to any one dashboard (same convention as RESIDUAL_BUCKET_B_RULES's
  // own "admin"/"home"/"user_profile" entries) - gates just this section of an otherwise-reachable
  // page (/settings is in PUBLIC_AUTHENTICATED_PATHS, ungated by any view), the way NewAdminPage.tsx
  // already gates its own admin-only tabs.
  const hasEngagementAccess = useHasView("track_engagement");

  const [draft, setDraft] = useState(userDetails);
  const [businessFunction, setBusinessFunction] = useState("");
  const [engagementOpen, setEngagementOpen] = useState(true);

  // fetchCurrentUserDetails() (not a bare FETCH_USER_DETAILS dispatch) - userSaga.ts's own handler
  // reads the action's payload as the email to fetch and no-ops entirely if it's missing. A bare
  // { type: FETCH_USER_DETAILS } used to fire here, which silently did nothing on this page (masked
  // on initial mount only because PrivateRoute.tsx's own correctly-payloaded dispatch happens to
  // run right after and populates the store anyway) - but the same broken dispatch after a save
  // (below) meant Redux's copy of the profile never actually refreshed post-save, leaving the Save
  // button stuck "dirty" and the page still showing pre-edit values even though the PUT succeeded.
  useEffect(() => {
    dispatch(fetchCurrentUserDetails());
  }, [dispatch]);

  useEffect(() => {
    setDraft(userDetails);
    setBusinessFunction(userDetails?.business_function || "");
  }, [userDetails]);

  useEffect(() => {
    setDraft((prevState: typeof userDetails) => ({ ...prevState, business_function: businessFunction }));
  }, [businessFunction]);

  const saveDisabled = _.isEqual(userDetails, draft);

  const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = evt;
    setDraft({ ...draft, [name]: value });
  };

  const handleSaveChanges = () => {
    // Only the fields that actually changed (and only from this page's own editable set) - not
    // the whole draft, which is a full copy of userDetails with just a couple of fields
    // overwritten. See getChangedFields.ts's own comment for why this matters to the backend.
    const changedFields = getChangedFields(userDetails, draft, EDITABLE_FIELDS);
    updateUserDetails(userDetails.email, changedFields).then((response) => {
      if (typeof response !== "string" && "error" in response) {
        toast.error(response.error);
      } else {
        toast.success("Profile updated successfully");
        dispatch(fetchCurrentUserDetails());
      }
    });
  };

  // Engagement is about the user's own usage of dashboards they can actually open - a locked
  // dashboard has no engagement data to show, so it's excluded here (DashboardAccessSection below
  // intentionally keeps reading the full, unfiltered `dashboards` list instead - its whole point
  // is surfacing the gaps). Archived dashboards (either sense - see isArchivedDashboard) are
  // excluded too - they belong only on the dedicated /archived page, not grouped in here as if
  // they were a normal category.
  const accessibleDashboards = useMemo(() => dashboards.filter((d) => d.has_access && !isArchivedDashboard(d)), [dashboards]);
  // Sorted alphabetically - categories themselves, and each category's own dashboards below.
  const categories = useMemo(
    () => Array.from(new Set(accessibleDashboards.map((d) => getPrimaryCategory(d)))).sort((a, b) => a.localeCompare(b)),
    [accessibleDashboards]
  );
  // Categories default open, similar to Dashboards page's own collapsible groups - each is
  // independently toggleable rather than one flat list.
  const [expandedEngagementGroups, setExpandedEngagementGroups] = useState<Record<string, boolean>>({});
  const toggleEngagementGroup = (category: string) =>
    setExpandedEngagementGroups((prev) => ({ ...prev, [category]: !(prev[category] ?? true) }));

  if (!userDetails || Object.keys(userDetails).length === 0) {
    return (
      <CedarShellLayout>
        <></>
      </CedarShellLayout>
    );
  }

  return (
    <CedarShellLayout>
      <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
        <div className="settings_page" data-test-id="settings-page">
          <div className="settings_page_header">
            <ProfilePicture firstname={userDetails.first_name || userDetails.email || " "} lastname={userDetails.last_name || " "} />
            <div className="settings_page_header_details">
              <h1>{`Hello ${userDetails.first_name || ""} ${userDetails.last_name || ""}`}</h1>
              <p>{userDetails.email}</p>
              <p>{userDetails.role}</p>
            </div>
          </div>

          <div className="settings_page_columns">
            <div className="settings_page_section">
              <div className="settings_page_section_header">
                <PersonOutlineIcon fontSize="small" />
                <h2 className="settings_page_section_title">My Profile</h2>
              </div>
              <div className="settings_page_section_divider" />
              <div className="settings_page_section_body">
                <TextInput
                  label="Firstname"
                  name="first_name"
                  value={draft.first_name}
                  id="settings_firstname"
                  onChange={handleInputChange}
                />
                <TextInput label="Lastname" name="last_name" value={draft.last_name} id="settings_lastname" onChange={handleInputChange} />
                <TextInput label="Email" name="email" value={draft.email} id="settings_email" readonly />
                <BusinessFunctionInput businessFunction={businessFunction} setBusinessFunction={setBusinessFunction} />
                <TextInput label="Department" name="department" value={draft.department} id="settings_department" onChange={handleInputChange} />
                <TextInput label="Role" name="role" value={draft.role} id="settings_role" readonly />
              </div>
            </div>

            <div className="settings_page_section">
              <div className="settings_page_section_header">
                <TuneIcon fontSize="small" />
                <h2 className="settings_page_section_title">My Preferences</h2>
              </div>
              <div className="settings_page_section_divider" />
              <div className="settings_page_section_body">
                <div className="settings_page_preference_row">
                  <span>Theme</span>
                  <DarkLightToggle />
                </div>
              </div>
            </div>
          </div>

          <div className="settings_page_actions">
            <button className="settings_button_primary" onClick={handleSaveChanges} disabled={saveDisabled}>
              Save changes
            </button>
          </div>

          {hasEngagementAccess && (
            <div className="settings_page_engagement">
              <div className="settings_engagement_header_row">
                <button
                  className="settings_engagement_title_button"
                  onClick={() => setEngagementOpen(!engagementOpen)}
                  aria-expanded={engagementOpen}
                >
                  <InsightsIcon fontSize="small" />
                  <h2 className="settings_page_section_title">Engagement</h2>
                </button>
                <button
                  className="settings_open_mixpanel_button"
                  onClick={() => window.open("https://mixpanel.com/home/", "_blank", "noopener,noreferrer")}
                  data-test-id="settings-open-mixpanel"
                >
                  <OpenInNewIcon fontSize="small" />
                  Open in Mixpanel
                </button>
                <button
                  className="settings_engagement_chevron"
                  onClick={() => setEngagementOpen(!engagementOpen)}
                  aria-expanded={engagementOpen}
                  aria-label={engagementOpen ? "Collapse" : "Expand"}
                >
                  {engagementOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
              </div>

              {engagementOpen &&
                categories.map((category) => {
                  const categoryDashboards = accessibleDashboards
                    .filter((d) => getPrimaryCategory(d) === category)
                    .sort((a, b) => a.name.localeCompare(b.name));
                  return (
                    <CollapsibleCategorySection
                      key={category}
                      label={
                        <>
                          <span
                            className="settings_engagement_group_dot"
                            style={{ backgroundColor: CATEGORY_COLORS[category] ?? DEFAULT_CATEGORY_COLOR }}
                          />
                          {category}
                        </>
                      }
                      testKey={category}
                      count={categoryDashboards.length}
                      isOpen={expandedEngagementGroups[category] ?? true}
                      onToggle={() => toggleEngagementGroup(category)}
                    >
                      <div className="settings_engagement_group_cards">
                        {categoryDashboards.map((dashboard) => (
                          <EngagementCard dashboard={dashboard} key={dashboard.key} />
                        ))}
                      </div>
                    </CollapsibleCategorySection>
                  );
                })}
            </div>
          )}

          <DashboardAccessSection dashboards={dashboards} />
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          draggable={false}
          pauseOnHover={false}
        />
      </ErrorBoundary>
    </CedarShellLayout>
  );
};
